<template>
  <div class="v2-pricing">
    <h3 class="v2-pricing__section-title">
      Pricing
    </h3>

    <div class="v2-pricing__row">
      <!-- Currency -->
      <div class="v2-pricing__field">
        <label class="v2-pricing__label">Currency</label>
        <input
          :value="currency"
          class="v2-pricing__input v2-pricing__input--disabled"
          disabled
        >
      </div>

      <!-- Wholesale Price -->
      <div class="v2-pricing__field">
        <label class="v2-pricing__label">
          Wholesale Price
          <span class="v2-pricing__required">*</span>
        </label>
        <validation-provider
          #default="{ errors }"
          name="Wholesale Price"
          :rules="isSameWholesalePrice ? 'required|price|non-zero' : ''"
        >
          <div
            class="v2-pricing__currency-input"
            :class="{
              'v2-pricing__currency-input--disabled': !isSameWholesalePrice,
              'v2-pricing__currency-input--updated': isSameWholesalePrice && isFieldUpdated(wholesalePrice, originalWholesalePrice),
              'v2-pricing__currency-input--error': errors[0],
            }"
          >
            <span class="v2-pricing__currency-label">{{ currency }}</span>
            <input
              v-model="wholesalePrice"
              type="text"
              class="v2-pricing__price-input"
              placeholder="Enter Wholesale Price"
              :disabled="!isSameWholesalePrice"
              @keypress="onlyAllowPositiveDecimal"
              @paste="blockInvalidPaste"
              @input="handlePriceChange('price', $event.target.value)"
            >
          </div>
          <span
            v-if="errors[0]"
            class="v2-pricing__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>

      <!-- Retail Price -->
      <div class="v2-pricing__field">
        <label class="v2-pricing__label">
          Retail Price
          <span class="v2-pricing__required">*</span>
        </label>
        <validation-provider
          #default="{ errors }"
          name="Retail Price"
          :rules="isSameRetailPrice ? 'required|price|non-zero' : ''"
        >
          <div
            class="v2-pricing__currency-input"
            :class="{
              'v2-pricing__currency-input--disabled': !isSameRetailPrice,
              'v2-pricing__currency-input--updated': isSameRetailPrice && isFieldUpdated(retailPrice, originalRetailPrice),
              'v2-pricing__currency-input--error': errors[0],
            }"
          >
            <span class="v2-pricing__currency-label">{{ currency }}</span>
            <input
              v-model="retailPrice"
              type="text"
              class="v2-pricing__price-input"
              placeholder="Enter Retail Price"
              :disabled="!isSameRetailPrice"
              @keypress="onlyAllowPositiveDecimal"
              @paste="blockInvalidPaste"
              @input="handlePriceChange('retailPrice', $event.target.value)"
            >
          </div>
          <span
            v-if="errors[0]"
            class="v2-pricing__error"
          >{{ errors[0] }}</span>
        </validation-provider>
      </div>
    </div>

    <v2-price-update-warning
      :show="showPriceUpdateWarning"
      @cancel="showPriceUpdateWarning = false"
      @close="showPriceUpdateWarning = false"
    />
  </div>
</template>

<script>
import store from '@/store'
import { UPDATE_PRODUCT_INFO } from '@/store/modules/productEdit.module'
import { ValidationProvider } from 'vee-validate'
import { blockInvalidPaste, onlyAllowPositiveDecimal } from '@/views/apps/product/edit/utils'
import V2PriceUpdateWarning from './V2PriceUpdateWarning.vue'

export default {
  name: 'V2Pricing',
  components: {
    ValidationProvider,
    V2PriceUpdateWarning,
  },
  data() {
    return {
      wholesalePrice: null,
      retailPrice: null,
      isSameWholesalePrice: false,
      isSameRetailPrice: false,
      showPriceUpdateWarning: false,
      priceChanged: false,
      originalWholesalePrice: null,
      originalRetailPrice: null,
      updatedPriceTypes: [],
    }
  },
  computed: {
    currency() {
      return store.state.productEdit.savedState.currency
    },
    items: {
      get() {
        return store.state.productEdit.savedState.items
      },
      set(value) {
        store.commit(UPDATE_PRODUCT_INFO, { fieldName: 'items', newValue: value })
      },
    },
    productId() {
      return store.state.productEdit.savedState._id
    },
  },
  mounted() {
    const wholesalePrices = store.state.productEdit.savedState.items?.map(item => item.price).sort((a, b) => a - b)
    const retailPrices = store.state.productEdit.savedState.items?.map(item => item.retailPrice).sort((a, b) => a - b)

    if (wholesalePrices.length > 0 && wholesalePrices.every(price => price === wholesalePrices[0])) {
      this.isSameWholesalePrice = true
      this.wholesalePrice = wholesalePrices[0]
      this.originalWholesalePrice = wholesalePrices[0]
    } else {
      this.wholesalePrice = `${wholesalePrices[0]} - ${wholesalePrices[wholesalePrices.length - 1]}`
      this.originalWholesalePrice = wholesalePrices[0]
    }
    if (retailPrices.length > 0 && retailPrices.every(price => price === retailPrices[0])) {
      this.isSameRetailPrice = true
      this.retailPrice = retailPrices[0]
      this.originalRetailPrice = retailPrices[0]
    } else {
      this.retailPrice = `${retailPrices[0]} - ${retailPrices[retailPrices.length - 1]}`
      this.originalRetailPrice = retailPrices[0]
    }
  },
  methods: {
    blockInvalidPaste,
    onlyAllowPositiveDecimal,
    isFieldUpdated(currentValue, originalValue) {
      return originalValue !== null && Number(currentValue) !== originalValue
    },
    handlePriceChange(type, newVal) {
      if (type === 'price') {
        this.wholesalePrice = newVal
      } else {
        this.retailPrice = newVal
      }

      this.items = this.items.map(item => ({
        ...item,
        price: Number(this.wholesalePrice),
        retailPrice: Number(this.retailPrice),
      }))

      const priceType = type === 'price' ? 'Wholesale Price' : 'Retail Price'
      this.updatedPriceTypes = [...new Set([...this.updatedPriceTypes, priceType])]

      const wholesalePriceChanged = this.originalWholesalePrice !== null && this.originalWholesalePrice !== Number(this.wholesalePrice)
      const retailPriceChanged = this.originalRetailPrice !== null && this.originalRetailPrice !== Number(this.retailPrice)
      this.priceChanged = wholesalePriceChanged || retailPriceChanged
    },
  },
}
</script>

<style lang="scss" scoped>
.v2-pricing {
  margin-top: var(--v2-space-6);

  &__section-title {
    font-family: var(--v2-font-display);
    font-size: 1rem;
    font-weight: 600;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-4);
    padding-bottom: var(--v2-space-2);
    border-bottom: 1px solid var(--v2-border);
  }

  &__row {
    display: flex;
    gap: var(--v2-space-4);

    > .v2-pricing__field {
      flex: 1;
      min-width: 0;
    }
  }

  &__field {
    margin-bottom: var(--v2-space-3);
  }

  &__label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--v2-muted-foreground);
    margin-bottom: var(--v2-space-1);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__required {
    color: var(--v2-destructive);
  }

  // Glass form field — matches V2Input recipe.
  &__input {
    width: 100%;
    padding: var(--v2-space-2) var(--v2-space-3);
    border: 1px solid var(--v2-glass-stroke);
    border-radius: var(--v2-radius-md);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    min-height: 38px;
    transition: background 0.15s ease, border-color 0.15s ease;

    &:hover:not(:disabled):not(:focus):not([readonly]) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      outline: none;
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }

    &--disabled {
      background: var(--v2-muted);
      backdrop-filter: none;
      cursor: not-allowed;
    }
  }

  &__currency-input {
    display: flex;
    align-items: center;
    border: 1px solid var(--v2-input);
    border-radius: var(--v2-radius-md);
    min-height: 38px;
    overflow: hidden;
    transition: border-color 0.15s ease;

    &:focus-within {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 2px rgba(116, 125, 238, 0.15);
    }

    &--disabled {
      background: var(--v2-muted);
    }

    &--updated {
      background: #e8f5e9 !important;
      border-color: #a5d6a7 !important;
    }

    &--error {
      border-color: var(--v2-destructive);
    }
  }

  &__currency-label {
    padding: 0 var(--v2-space-3);
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--v2-foreground);
    white-space: nowrap;
  }

  &__price-input {
    flex: 1;
    border: none;
    padding: var(--v2-space-2) var(--v2-space-2);
    font-size: 0.875rem;
    font-family: var(--v2-font-sans);
    color: var(--v2-foreground);
    background: transparent;
    min-width: 0;

    &:focus {
      outline: none;
    }

    &:disabled {
      cursor: not-allowed;
      color: var(--v2-muted-foreground);
    }
  }

  &__error {
    display: block;
    font-size: 0.6875rem;
    color: var(--v2-destructive);
    margin-top: var(--v2-space-1);
  }
}
</style>
